/*
  E.N.D. music player
  - <end-music-player> header icon that expands into a Spotify embed (Spotify iFrame API).
  - Seamless page changes (EndNav) so the music keeps playing while people browse.
    EndNav only switches on after the music has started, and never in the theme editor.
    Cart, account, checkout and a few system paths always load normally, which stops the music.
*/
(function () {
  if (window.__endMusicPlayer) return;
  window.__endMusicPlayer = true;

  var API_SRC = 'https://open.spotify.com/embed/iframe-api/v1';
  var MOBILE = '(max-width: 989px)';

  /* ---------- Spotify iFrame API loader ---------- */
  var apiPromise = null;
  function loadSpotifyApi() {
    if (apiPromise) return apiPromise;
    apiPromise = new Promise(function (resolve, reject) {
      var prev = window.onSpotifyIframeApiReady;
      window.onSpotifyIframeApiReady = function (api) {
        resolve(api);
        if (typeof prev === 'function') { try { prev(api); } catch (e) {} }
      };
      var s = document.createElement('script');
      s.src = API_SRC;
      s.async = true;
      s.onerror = function () { apiPromise = null; reject(new Error('Spotify API failed to load')); };
      document.head.appendChild(s);
      setTimeout(function () { reject(new Error('Spotify API timed out')); }, 15000);
    });
    return apiPromise;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* ---------- Seamless navigation ---------- */
  var SYSTEM_PATHS = /^\/(?:[a-z]{2}(?:-[a-z]{2,4})?\/)?(?:cart|account|checkout|checkouts|apps|services|tools|admin|password|challenge|cdn|wpm|orders|customer_authentication|discount|gift_cards|localization)(?:\/|$)/i;
  var FILE_EXT = /\.(?:pdf|zip|jpe?g|png|gif|webp|avif|svg|mp4|mov|mp3|wav|xml|json|txt|csv|ics)$/i;

  var EndNav = {
    enabled: false,
    busy: null,
    headerId: null,
    extraExclude: [],
    currentKey: location.pathname + location.search,

    init: function (opts) {
      this.headerId = opts.headerId || this.headerId;
      this.extraExclude = opts.exclude || this.extraExclude;
    },

    enable: function () {
      if (this.enabled || isDesignMode()) return;
      if (!window.fetch || !window.DOMParser || !history.pushState) return;
      if (!this.headerId || !document.querySelector('.transition-content')) return;
      this.enabled = true;
      try {
        history.replaceState(this._state({ endScroll: window.scrollY }), '', location.href);
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      } catch (e) {}
      document.addEventListener('click', this._onClick = this.onClick.bind(this));
      document.addEventListener('submit', this._onSubmit = this.onSubmit.bind(this));
      window.addEventListener('popstate', this._onPop = this.onPop.bind(this));
    },

    _state: function (extra) {
      var st = {};
      var cur = history.state;
      if (cur && typeof cur === 'object') { for (var k in cur) st[k] = cur[k]; }
      st.endNav = true;
      if (extra) { for (var j in extra) st[j] = extra[j]; }
      return st;
    },

    excluded: function (url) {
      if (url.origin !== location.origin) return true;
      if (!/^https?:$/.test(url.protocol)) return true;
      if (SYSTEM_PATHS.test(url.pathname)) return true;
      if (FILE_EXT.test(url.pathname)) return true;
      for (var i = 0; i < this.extraExclude.length; i++) {
        var p = this.extraExclude[i];
        if (p && url.pathname.indexOf(p) === 0) return true;
      }
      return false;
    },

    onClick: function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.hasAttribute('download') || a.closest('[data-end-reload]')) return;
      var target = (a.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || /^(?:mailto|tel|sms|javascript):/i.test(href)) return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (this.excluded(url)) return;
      // Same page, only the #hash changes: let the browser scroll.
      if (url.hash && url.pathname === location.pathname && url.search === location.search) return;
      e.preventDefault();
      this.navigate(url.href, { push: true });
    },

    onSubmit: function (e) {
      if (e.defaultPrevented) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var method = (form.getAttribute('method') || 'get').toLowerCase();
      if (method !== 'get' || form.closest('[data-end-reload]')) return;
      var target = (form.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      var url;
      try { url = new URL(form.getAttribute('action') || location.href, location.href); } catch (err) { return; }
      if (this.excluded(url)) return;
      var params;
      try { params = new URLSearchParams(new FormData(form, e.submitter || undefined)); }
      catch (err) { params = new URLSearchParams(new FormData(form)); }
      url.search = params.toString();
      e.preventDefault();
      this.navigate(url.href, { push: true });
    },

    onPop: function (e) {
      var key = location.pathname + location.search;
      var st = e.state;
      if (key === this.currentKey) return; // hash-only change
      if (!st || !st.endNav) { location.reload(); return; }
      this.navigate(location.href, { push: false, scroll: st.endScroll || 0 });
    },

    hard: function (href) {
      window.location.assign(href);
    },

    navigate: function (href, opts) {
      var self = this;
      opts = opts || {};
      var url = new URL(href, location.href);
      if (this.busy) { try { this.busy.abort(); } catch (e) {} }
      var ctrl = window.AbortController ? new AbortController() : null;
      this.busy = ctrl;
      try { history.replaceState(this._state({ endScroll: window.scrollY }), '', location.href); } catch (e) {}
      document.documentElement.classList.add('end-nav-loading');

      var finalUrl;
      return fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        finalUrl = new URL(res.url || url.href);
        var type = res.headers.get('content-type') || '';
        if ((!res.ok && res.status !== 404) || type.indexOf('text/html') === -1 || self.excluded(finalUrl)) {
          throw new Error('hard');
        }
        return res.text();
      }).then(function (html) {
        if (ctrl && ctrl.signal.aborted) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var curContent = document.querySelector('.transition-content');
        var newContent = doc.querySelector('.transition-content');
        // No header on the new page (or a different layout): load it normally, which stops the music.
        if (!curContent || !newContent || !doc.getElementById(self.headerId) || !doc.querySelector('end-music-player')) {
          throw new Error('hard');
        }
        if (url.hash && !finalUrl.hash) finalUrl.hash = url.hash;
        if (opts.push !== false) {
          history.pushState(self._state({ endScroll: 0 }), '', finalUrl.href);
        }
        self.currentKey = location.pathname + location.search;
        return self.swap(doc, curContent, newContent).then(function () {
          self.afterSwap(finalUrl, opts);
        });
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.hard(url.href);
      }).then(function () {
        if (self.busy === ctrl) {
          self.busy = null;
          document.documentElement.classList.remove('end-nav-loading');
        }
      });
    },

    swap: function (doc, curContent, newContent) {
      var self = this;
      var inserted = [];
      var haveSrc = {};
      document.querySelectorAll('script[src]').forEach(function (s) { haveSrc[s.src.split('#')[0]] = true; });

      this.closeOverlays();

      // <head>: title, SEO tags, any stylesheet the new page needs.
      document.title = doc.title;
      ['meta[name="description"]', 'link[rel="canonical"]', 'meta[property^="og:"]', 'meta[name^="twitter:"]'].forEach(function (sel) {
        document.head.querySelectorAll(sel).forEach(function (n) { n.remove(); });
        doc.head.querySelectorAll(sel).forEach(function (n) { document.head.appendChild(n.cloneNode(true)); });
      });
      var haveCss = {};
      document.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) { haveCss[l.href] = true; });
      doc.head.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) {
        var abs = new URL(l.getAttribute('href'), location.href).href;
        if (!haveCss[abs]) document.head.appendChild(l.cloneNode(true));
      });
      var haveStyle = {};
      document.head.querySelectorAll('style').forEach(function (s) { haveStyle[s.textContent] = true; });
      doc.head.querySelectorAll('style').forEach(function (s) {
        if (!haveStyle[s.textContent]) document.head.appendChild(s.cloneNode(true));
      });

      // Header group sections other than the header itself (e.g. announcement bar).
      var curGroup = Array.prototype.slice.call(document.querySelectorAll('.shopify-section-group-header-group'));
      var newGroup = Array.prototype.slice.call(doc.querySelectorAll('.shopify-section-group-header-group'));
      var headerEl = document.getElementById(this.headerId);
      newGroup.forEach(function (n) {
        if (n.id === self.headerId) return;
        var old = document.getElementById(n.id);
        var node = document.importNode(n, true);
        if (old) old.replaceWith(node); else headerEl.parentNode.insertBefore(node, headerEl);
        inserted.push(node);
      });
      curGroup.forEach(function (o) {
        if (o.id !== self.headerId && !doc.getElementById(o.id)) o.remove();
      });

      // Header: keep it (the player lives here) but copy the active-link state.
      var newHeader = doc.getElementById(this.headerId);
      if (headerEl && newHeader) {
        var oldLinks = headerEl.querySelectorAll('a[href]:not(.end-mp__follow)');
        var newLinks = newHeader.querySelectorAll('a[href]:not(.end-mp__follow)');
        if (oldLinks.length === newLinks.length) {
          for (var i = 0; i < oldLinks.length; i++) {
            oldLinks[i].className = newLinks[i].className;
            if (newLinks[i].hasAttribute('aria-current')) oldLinks[i].setAttribute('aria-current', newLinks[i].getAttribute('aria-current'));
            else oldLinks[i].removeAttribute('aria-current');
          }
        }
      }

      // Main content + footer.
      var frag = document.createDocumentFragment();
      Array.prototype.slice.call(newContent.childNodes).forEach(function (n) {
        var node = document.importNode(n, true);
        frag.appendChild(node);
        if (node.nodeType === 1) inserted.push(node);
      });
      curContent.replaceChildren(frag);

      // Sections that sit directly in <body> (outside the page wrapper).
      var bodySel = ':scope > .shopify-section';
      var curBody = Array.prototype.slice.call(document.body.querySelectorAll(bodySel));
      var newBody = Array.prototype.slice.call(doc.body.querySelectorAll(bodySel));
      newBody.forEach(function (n) {
        var old = document.getElementById(n.id);
        var node = document.importNode(n, true);
        if (old) old.replaceWith(node);
        else {
          var anchor = document.querySelector('.transition-body');
          if (anchor && anchor.nextSibling) anchor.parentNode.insertBefore(node, anchor.nextSibling);
          else document.body.appendChild(node);
        }
        inserted.push(node);
      });
      curBody.forEach(function (o) { if (!doc.getElementById(o.id)) o.remove(); });

      // <body> template classes.
      var keep = Array.prototype.filter.call(document.body.classList, function (c) {
        return !/^template-/.test(c) && c !== 'unloading';
      });
      document.body.className = keep.join(' ');
      doc.body.classList.forEach(function (c) { document.body.classList.add(c); });
      document.body.classList.add('loaded');

      return this.runScripts(inserted, haveSrc);
    },

    runScripts: function (roots, have) {
      have = have || {};
      var list = [];
      roots.forEach(function (r) {
        if (r.tagName === 'SCRIPT') list.push(r);
        if (r.querySelectorAll) r.querySelectorAll('script').forEach(function (s) { list.push(s); });
      });
      var chain = Promise.resolve();
      list.forEach(function (old) {
        chain = chain.then(function () {
          var type = (old.getAttribute('type') || '').trim().toLowerCase();
          var isModule = type === 'module';
          var isJs = !type || isModule || /^(?:text|application)\/(?:x-)?(?:java|ecma)script$/.test(type);
          if (!isJs || !old.parentNode) return;
          var s = document.createElement('script');
          for (var i = 0; i < old.attributes.length; i++) s.setAttribute(old.attributes[i].name, old.attributes[i].value);
          if (old.src) {
            var key = old.src.split('#')[0];
            // Already on the page: don't load it twice (its own section:load listeners re-run it).
            if (have[key]) return;
            have[key] = true;
            return new Promise(function (resolve) {
              s.onload = s.onerror = function () { resolve(); };
              old.replaceWith(s);
              if (s.async) resolve();
              setTimeout(resolve, 8000);
            });
          }
          var code = old.textContent || '';
          // Wrap classic scripts in a block so repeated visits don't redeclare top-level let/const/class.
          if (!isModule && /\b(?:let|const|class)\s/.test(code) && !/^\s*['"]use strict['"]/.test(code)) {
            code = '{\n' + code + '\n}';
          }
          s.textContent = code;
          old.replaceWith(s);
        }).catch(function () {});
      });
      return chain;
    },

    closeOverlays: function () {
      var header = document.getElementById(this.headerId);
      if (header) {
        header.querySelectorAll('details[open]').forEach(function (d) {
          if (d.closest('end-music-player')) return;
          var summary = d.querySelector(':scope > summary');
          if (summary) summary.click();
          if (d.hasAttribute('open')) d.removeAttribute('open');
        });
      }
      try {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.close === 'function' && drawer.querySelector('details[open]')) drawer.close();
      } catch (e) {}
      try {
        if (window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.hide === 'function') window.Rebuy.SmartCart.hide();
      } catch (e) {}
    },

    afterSwap: function (url, opts) {
      // Let theme + E.N.D. scripts set up the new page sections. Only sections inside <main>,
      // so popups, age gate and cookie banner don't re-open on every page change.
      var main = document.getElementById('MainContent');
      var sections = main ? main.querySelectorAll('[id^="shopify-section-"]') : [];
      Array.prototype.forEach.call(sections, function (sec) {
        var id = sec.id.replace(/^shopify-section-/, '');
        try { sec.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } })); } catch (e) {}
      });
      try { document.dispatchEvent(new CustomEvent('page:loaded')); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('end:navigated', { detail: { url: url.href } })); } catch (e) {}

      // Scroll + focus.
      var y = typeof opts.scroll === 'number' ? opts.scroll : 0;
      var hashEl = url.hash ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null;
      if (hashEl && opts.push !== false) hashEl.scrollIntoView();
      else window.scrollTo(0, y);
      if (main) { try { main.focus({ preventScroll: true }); } catch (e) {} }

      setTimeout(function () { window.dispatchEvent(new Event('resize')); document.dispatchEvent(new Event('scroll', { bubbles: true })); }, 60);
    }
  };
  window.EndNav = EndNav;

  /* ---------- <end-music-player> ---------- */
  class EndMusicPlayer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.toggleBtn = this.querySelector('.end-mp__toggle');
      this.panel = this.querySelector('.end-mp__panel');
      this.embed = this.querySelector('[data-end-mp-embed]');
      this.playlist = this.dataset.playlist;
      this.autoplay = this.dataset.autoplay !== 'false';
      this.seamless = this.dataset.seamless !== 'false';
      this.appleSrc = this.dataset.appleEmbed || '';
      this.appleEl = this.querySelector('[data-end-mp-apple]');
      this.service = 'spotify';
      if (this.appleSrc) {
        try { if (localStorage.getItem('endMusicService') === 'apple') this.service = 'apple'; } catch (e) {}
      }
      this.applyService();

      var section = this.closest('.shopify-section');
      EndNav.init({
        headerId: section ? section.id : null,
        exclude: (this.dataset.exclude || '').split(',').map(function (p) { return p.trim(); }).filter(Boolean)
      });

      this.toggleBtn.addEventListener('click', () => this.toggle());
      // Phone-only music button next to the menu button (rendered separately in the header).
      this.mToggles = Array.prototype.slice.call(document.querySelectorAll('[data-end-mp-mobile-toggle]'));
      this.mToggles.forEach((b) => b.addEventListener('click', () => this.toggle()));
      var playBtn = this.querySelector('[data-end-mp-play]');
      if (playBtn) playBtn.addEventListener('click', () => this.togglePlay());
      var swapBtn = this.querySelector('[data-end-mp-swap]');
      if (swapBtn) swapBtn.addEventListener('click', () => this.swap());
      var closeBtn = this.querySelector('[data-end-mp-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close(true));
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(true); });
      document.addEventListener('click', (e) => {
        if (!this.classList.contains('is-open') || this.contains(e.target)) return;
        if (this.mToggles.some(function (b) { return b.contains(e.target); })) return;
        this.close();
      });
      this._onScroll = () => this.position();
      window.addEventListener('resize', this._onScroll, { passive: true });
      this.syncFont();
    }

    syncFont() {
      var ref = document.querySelector('.header__menu-item, .header .list-menu__item, .header nav a');
      if (!ref) return;
      var cs = getComputedStyle(ref);
      this.style.setProperty('--end-mp-font', cs.fontFamily);
      this.style.setProperty('--end-mp-weight', cs.fontWeight);
      this.style.setProperty('--end-mp-ls', cs.letterSpacing === 'normal' ? '.15em' : cs.letterSpacing);
      this.style.setProperty('--end-mp-tt', cs.textTransform);
    }

    position() {
      if (!this.classList.contains('is-open')) return;
      if (!window.matchMedia(MOBILE).matches) {
        // Desktop: drop the panel below the whole header (under the menu row), lined up with the right edge of the icons.
        var hdr = this.closest('header') || this.closest('.shopify-section');
        var icons = this.closest('.header__right') || this;
        var me = this.getBoundingClientRect();
        if (hdr) this.style.setProperty('--end-mp-dtop', Math.round(hdr.getBoundingClientRect().bottom - me.top + 8) + 'px');
        this.style.setProperty('--end-mp-dright', Math.round(me.right - icons.getBoundingClientRect().right) + 'px');
        return;
      }
      var header = this.closest('header') || this.closest('.shopify-section');
      if (!header) return;
      var bottom = Math.max(header.getBoundingClientRect().bottom, 0);
      this.style.setProperty('--end-mp-top', Math.round(bottom + 8) + 'px');
    }

    toggle() {
      if (this.classList.contains('is-open')) this.close(); else this.open();
    }

    setPlaying(playing) {
      this.classList.toggle('is-playing', playing);
      this.mToggles.forEach(function (b) { b.classList.toggle('is-playing', playing); });
    }

    togglePlay() {
      if (this.controller) { try { this.controller.togglePlay(); } catch (e) {} return; }
      this._playOnReady = true;
      if (!this._creating) this.createPlayer();
    }

    open() {
      this.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.mToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      this.position();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      // Switch on seamless page changes as soon as the player is opened, so music started
      // inside the Spotify player keeps going even if Spotify doesn't report "playing" first.
      if (this.seamless) EndNav.enable();
      this.startService();
    }

    startService() {
      if (this.service === 'apple') this.createApple();
      else if (!this.controller && !this._creating) this.createPlayer();
    }

    // Spotify <-> Apple Music. Remembers the choice for the next visit.
    swap() {
      if (!this.appleSrc) return;
      var toApple = this.service !== 'apple';
      if (toApple) {
        if (this.controller) { try { this.controller.pause(); } catch (e) {} }
        this.service = 'apple';
      } else {
        // Removing the Apple player is the only way to stop it (it has no remote control).
        if (this.appleEl) this.appleEl.innerHTML = '';
        this.service = 'spotify';
      }
      try { localStorage.setItem('endMusicService', this.service); } catch (e) {}
      this.setPlaying(false);
      this.applyService();
      this.startService();
      if (!toApple && this.controller && this.autoplay) { try { this.controller.resume(); } catch (e) {} }
      this.position();
    }

    applyService() {
      var apple = this.service === 'apple';
      this.classList.toggle('is-apple', apple);
      var follow = this.querySelector('[data-end-mp-follow]');
      if (follow) follow.href = apple ? follow.dataset.hrefApple : follow.dataset.hrefSpotify;
      var swapBtn = this.querySelector('[data-end-mp-swap]');
      if (swapBtn) swapBtn.setAttribute('aria-label', apple ? 'Switch to Spotify' : 'Switch to Apple Music');
    }

    createApple() {
      if (!this.appleEl || this.appleEl.querySelector('iframe')) return;
      var f = document.createElement('iframe');
      f.src = this.appleSrc;
      f.title = 'Apple Music player';
      f.loading = 'eager';
      f.setAttribute('allow', 'autoplay *; encrypted-media *; clipboard-write');
      f.setAttribute('sandbox', 'allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation');
      this.appleEl.appendChild(f);
    }

    close(returnFocus) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.mToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      window.removeEventListener('scroll', this._onScroll);
      if (returnFocus) this.toggleBtn.focus();
    }

    createPlayer() {
      this._creating = true;
      this.classList.add('is-loading');
      loadSpotifyApi().then((api) => {
        var host = document.createElement('div');
        this.embed.appendChild(host);
        api.createController(host, {
          uri: 'spotify:playlist:' + this.playlist,
          width: 300,
          height: 80
        }, (controller) => {
          this.controller = controller;
          controller.addListener('ready', () => {
            this.classList.remove('is-loading');
            if (this.autoplay || this._playOnReady) { try { controller.play(); } catch (e) {} }
          });
          controller.addListener('playback_update', (e) => {
            var d = (e && e.data) || {};
            var playing = d.isPaused === false;
            this.setPlaying(playing);
            if (playing && this.seamless) EndNav.enable();
          });
        });
      }).catch(() => {
        // Spotify couldn't load (blocked or offline): fall back to opening the playlist.
        this.classList.remove('is-loading');
        this._creating = false;
        this.embed.innerHTML = '';
        var a = document.createElement('a');
        a.href = 'https://open.spotify.com/playlist/' + this.playlist;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = 'Open the playlist in Spotify';
        a.style.cssText = 'display:flex;align-items:center;justify-content:center;height:80px;color:#fff;font-size:12px;';
        this.embed.appendChild(a);
      });
    }
  }

  if (!customElements.get('end-music-player')) customElements.define('end-music-player', EndMusicPlayer);
})();
